const allowed_rich_text_tags = new Set([
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    'ul',
    'ol',
    'li',
    'a',
    'div',
    'span'
]);

const blocked_rich_text_tags = new Set([
    'script',
    'style',
    'iframe',
    'object',
    'embed',
    'meta',
    'link'
]);

const rich_text_markup_pattern = /<\/?[a-z][\s\S]*>/i;

const escape_html = (value = '') => {
    return `${value || ''}`
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const safe_anchor_href = (hrefValue = '') => {
    const normalizedHref = `${hrefValue || ''}`.trim();

    if (!normalizedHref.length) {
        return '';
    }

    if (/^https?:\/\//i.test(normalizedHref) || /^mailto:/i.test(normalizedHref)) {
        return normalizedHref;
    }

    return '';
};

const html_text_content = (value = '') => {
    const rawValue = `${value || ''}`;

    if (!rawValue.length) {
        return '';
    }

    if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
        return rawValue
            .replace(/<[^>]+>/g, ' ')
            .replace(/&nbsp;/gi, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    const parser = new DOMParser();
    const documentValue = parser.parseFromString(`<div>${rawValue}</div>`, 'text/html');
    const rootNode = documentValue.body.firstElementChild || documentValue.body;
    return `${rootNode.textContent || ''}`.replace(/\s+/g, ' ').trim();
};

const contains_rich_text_markup = (value = '') => {
    return rich_text_markup_pattern.test(`${value || ''}`);
};

const plain_text_to_rich_text_html = (value = '') => {
    const normalizedValue = `${value || ''}`.replace(/\r\n/g, '\n').trim();

    if (!normalizedValue.length) {
        return '';
    }

    const paragraphs = normalizedValue
        .split(/\n{2,}/)
        .map((paragraphValue) => paragraphValue.trim())
        .filter((paragraphValue) => paragraphValue.length > 0);

    return paragraphs
        .map((paragraphValue) => {
            return `<p>${escape_html(paragraphValue).replace(/\n/g, '<br/>')}</p>`;
        })
        .join('');
};

const sanitize_rich_text_html = (value = '') => {
    const rawValue = `${value || ''}`.trim();

    if (!rawValue.length) {
        return '';
    }

    if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
        if (contains_rich_text_markup(rawValue)) {
            return rawValue;
        }

        return plain_text_to_rich_text_html(rawValue);
    }

    const parser = new DOMParser();
    const documentValue = parser.parseFromString(`<div>${rawValue}</div>`, 'text/html');
    const rootNode = documentValue.body.firstElementChild || documentValue.body;

    const sanitize_node = (node) => {
        const childNodes = [...(node?.children || [])];

        childNodes.forEach((childNode) => {
            const tagName = `${childNode?.tagName || ''}`.trim().toLowerCase();

            if (blocked_rich_text_tags.has(tagName)) {
                childNode.remove();
                return;
            }

            if (!allowed_rich_text_tags.has(tagName)) {
                const replacementNodes = [...(childNode?.childNodes || [])];

                replacementNodes.forEach((replacementNode) => {
                    childNode.parentNode?.insertBefore(replacementNode, childNode);
                });

                childNode.remove();
                return;
            }

            const attributes = [...(childNode?.attributes || [])];
            attributes.forEach((attributeItem) => {
                const attributeName = `${attributeItem?.name || ''}`.trim().toLowerCase();
                const attributeValue = `${attributeItem?.value || ''}`.trim();

                if (tagName === 'a' && attributeName === 'href') {
                    const safeHref = safe_anchor_href(attributeValue);

                    if (!safeHref.length) {
                        childNode.removeAttribute('href');
                        return;
                    }

                    childNode.setAttribute('href', safeHref);
                    childNode.setAttribute('target', '_blank');
                    childNode.setAttribute('rel', 'noopener noreferrer');
                    return;
                }

                childNode.removeAttribute(attributeName);
            });

            sanitize_node(childNode);
        });
    };

    sanitize_node(rootNode);
    return `${rootNode.innerHTML || ''}`.trim();
};

const normalized_rich_text_editor_html = (value = '') => {
    const rawValue = `${value || ''}`.trim();

    if (!rawValue.length) {
        return '';
    }

    if (contains_rich_text_markup(rawValue)) {
        return sanitize_rich_text_html(rawValue);
    }

    return plain_text_to_rich_text_html(rawValue);
};

const rich_text_has_content = (value = '') => {
    return html_text_content(value).length > 0;
};

const rich_text_comment_html_for_preview = (value = '') => {
    const normalizedEditorHtml = normalized_rich_text_editor_html(value);

    if (!normalizedEditorHtml.length) {
        return '';
    }

    return sanitize_rich_text_html(normalizedEditorHtml);
};

export {
    normalized_rich_text_editor_html,
    rich_text_comment_html_for_preview,
    rich_text_has_content,
    sanitize_rich_text_html
};
